// Fountain.js with search functionality
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Fountain.css';
import AuthModal from './AuthModal';
import Notification from './Notification';
import { Target, LogOut, User, Search, Save, X, Menu, ChevronLeft, HelpCircle, Settings, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { applyFountainScale } from './lib/fountainScale';

function Fountain() {
  // Separate state for inner and outer images
  const [outerImages, setOuterImages] = useState([]);
  const [innerImages, setInnerImages] = useState([]);
  // New state for preloaded images (next 8 images ready to use)
  const [preloadedImages, setPreloadedImages] = useState([]);
  // Track loading state for each image
  const [loadedImages, setLoadedImages] = useState({});
  // Track animation states for keyboard feedback
  const [animationStates, setAnimationStates] = useState({
    north: false,
    east: false, 
    south: false,
    west: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '' });
  
  // Image source filtering state - all sources enabled by default
  const [imageSources, setImageSources] = useState({
    arena: true,
    flickr: true,
    archive: true,
    wikimedia: true,
    imgur: true
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchPanelRef = useRef(null);
  
  // Session state - tracks saved images in current session
  const [sessionImages, setSessionImages] = useState([]);
  const sessionThumbnailsRef = useRef(null);
  
  // Sidebar minimization state
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(true);
  
  // Session panel minimization state
  const [isSessionMinimized, setIsSessionMinimized] = useState(true);
  
  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Settings panel state
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(() => {
    const stored = localStorage.getItem('timerEnabled');
    return stored === null ? true : stored === 'true';
  });
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  // Session timer state (60 seconds)
  const [timeRemaining, setTimeRemaining] = useState(60);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // Track which image URLs have been pre-loaded in the browser cache
  const preloadedUrlsRef = useRef(new Set());

  // Pre-load image thumbnails into browser cache so they display instantly on rotation
  const preloadBrowserImages = useCallback((images) => {
    images.forEach(item => {
      const url = item.thumbnailUrl;
      if (url && !preloadedUrlsRef.current.has(url)) {
        preloadedUrlsRef.current.add(url);
        const img = new Image();
        img.src = url;
      }
    });
  }, []);

  // Random prompt word for the session
  const PROMPT_WORDS = [
    'drift', 'echo', 'bloom', 'rust', 'hollow', 'tender', 'fracture', 'silk',
    'ember', 'vapor', 'thorn', 'dust', 'ripple', 'ghost', 'pulse', 'shadow',
    'murmur', 'stone', 'glass', 'honey', 'orbit', 'moss', 'static', 'bone',
    'velvet', 'ash', 'tide', 'fog', 'spark', 'loom', 'grain', 'haze',
    'wander', 'fold', 'sway', 'blush', 'glow', 'trace', 'melt', 'nest'
  ];
  const [sessionPrompt] = useState(() => PROMPT_WORDS[Math.floor(Math.random() * PROMPT_WORDS.length)]);
  const [pendingSessionEnd, setPendingSessionEnd] = useState(false);


  const fetchImages = async (searchTerm = '') => {
    try {
      setLoading(true);
      
      // Choose endpoint based on search mode
      let url;
      if (searchTerm && isSearchMode) {
        // For search, only use Archive.org for now (most reliable)
        const searchSources = ['archive'];
        const sourcesParam = `&sources=${searchSources.join(',')}`;
        url = `/api/search-images?q=${encodeURIComponent(searchTerm)}&count=16${sourcesParam}`;
      } else {
        // For browsing, use all enabled sources - fetch large batch for smooth skipping
        const enabledSources = Object.keys(imageSources).filter(source => imageSources[source]);
        const sourcesParam = enabledSources.length > 0 ? `&sources=${enabledSources.join(',')}` : '';
        url = `/api/cached-images?count=48${sourcesParam}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();

      // DEBUG: Log what we received
      console.log('=== FOUNTAIN DEBUG ===');
      console.log('Total images received:', data.length);
      const sources = {};
      data.forEach(img => sources[img.source] = (sources[img.source] || 0) + 1);
      console.log('Source breakdown:', sources);
      console.log('First 8 images:');
      data.slice(0, 8).forEach((img, i) => {
        console.log(`  ${i+1}. ${img.source}: ${img.name}`);
      });
      console.log('====================');

      // Display the first 8 images
      setOuterImages(data.slice(0, 4));
      setInnerImages(data.slice(4, 8));

      // Preload all remaining images
      setPreloadedImages(data.slice(8));

      // Pre-load all thumbnails into browser cache
      preloadBrowserImages(data);

      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Publish --fountain-scale, which Fountain.css consumes. Coalesced into an
  // animation frame because visualViewport fires continuously while mobile
  // browser chrome collapses, and writing a custom property forces a restyle.
  useEffect(() => {
    let frame = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        applyFountainScale();
      });
    };

    applyFountainScale();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', schedule);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      if (vv) vv.removeEventListener('resize', schedule);
      // Fountain owns this property; other routes must not inherit its scale.
      document.documentElement.style.removeProperty('--fountain-scale');
    };
  }, []);

  // Initial fetch - get 16 images to populate both circles and preload buffer
  useEffect(() => {
    if (isSearchMode && searchQuery) {
      fetchImages(searchQuery);
    } else if (!isSearchMode) {
      fetchImages();
    }
  }, [imageSources, isSearchMode]); // Re-fetch when sources or search mode change

  // Handle click outside search panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchPanelRef.current && 
          !searchPanelRef.current.contains(event.target) && 
          isSearchExpanded && 
          searchQuery.trim() === '') {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded, searchQuery]);

  // Background fetch to replenish preloaded images when needed
  const fetchMoreImages = async (count = 8) => {
    try {
      console.log("Fetching more images from cache...");
      
      // Build sources query parameter for background fetch
      const enabledSources = Object.keys(imageSources).filter(source => imageSources[source]);
      const sourcesParam = enabledSources.length > 0 ? `&sources=${enabledSources.join(',')}` : '';
      
      // Use search endpoint if in search mode, otherwise use cached endpoint
      let url;
      if (isSearchMode && searchQuery) {
        url = `/api/search-images?q=${encodeURIComponent(searchQuery)}&count=${count}${sourcesParam}`;
        console.log("Background fetching search results for:", searchQuery);
      } else {
        // Use cached endpoint for better performance
        url = `/api/cached-images?count=${count}${sourcesParam}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const newImages = await response.json();
      return newImages;
    } catch (err) {
      console.error('Background fetch error:', err);
      return [];
    }
  };

  // Rotation with preloaded images - updates every 10 seconds
  useEffect(() => {
    if (innerImages.length === 0 || preloadedImages.length === 0) {
      // Skip until we have both inner and preloaded images
      return;
    }
    
    const rotationInterval = setInterval(async () => {
      // Pre-mark images as loaded if their thumbnails are already in browser cache
      const nextInner = preloadedImages.slice(0, 4);
      const preMarked = {};
      [...innerImages, ...nextInner].forEach(item => {
        if (item.thumbnailUrl && preloadedUrlsRef.current.has(item.thumbnailUrl)) {
          preMarked[item.id] = true;
        }
      });
      setLoadedImages(preMarked);

      // Use preloaded images for immediate rotation
      // Inner images become outer
      setOuterImages([...innerImages]);

      // First 4 preloaded images become inner
      setInnerImages(nextInner);
      
      // Remove the used preloaded images and keep the rest
      const remainingPreloaded = preloadedImages.slice(4);
      setPreloadedImages(remainingPreloaded);
      
      // If our preloaded buffer is getting low, fetch more
      // But only if NOT in search mode to avoid API rate limiting
      if (remainingPreloaded.length < 16 && !isSearchMode) {
        const countNeeded = 36; // Large buffer for seamless loading
        console.log(`Preloaded images low (${remainingPreloaded.length}), fetching ${countNeeded} more...`);
        
        // Fetch more images and add to the preload buffer
        const newImages = await fetchMoreImages(countNeeded);
        preloadBrowserImages(newImages);
        setPreloadedImages([...remainingPreloaded, ...newImages]);
      }
    }, 4000); // 4 second interval
    
    return () => {
      clearInterval(rotationInterval);
    };
  }, [innerImages, preloadedImages]);


  // Function to trigger animation for a direction
  const triggerAnimation = (direction) => {
    // Set the animation state for this direction to true
    setAnimationStates(prev => ({
      ...prev,
      [direction]: true
    }));
    
    // Reset the animation state after animation completes
    setTimeout(() => {
      setAnimationStates(prev => ({
        ...prev,
        [direction]: false
      }));
    }, 300); // Match animation duration in CSS
  };

  // Ref-based guard to prevent rapid successive calls (avoids stale closure issues)
  const isRotatingRef = useRef(false);

  // Force next ring rotation
  const forceNextRing = () => {
    // Prevent rapid successive calls
    if (isRotatingRef.current) {
      return;
    }

    isRotatingRef.current = true;

    try {
      if (innerImages.length === 0) {
        // No images at all, need to fetch
        fetchImages(isSearchMode ? searchQuery : '');
        return;
      }

      if (preloadedImages.length === 0) {
        // Have current images but buffer empty - fetch in background, don't block
        fetchMoreImages(36).then(newImages => {
          if (newImages.length > 0) {
            preloadBrowserImages(newImages);
            setPreloadedImages(newImages);
          }
        });
        return;
      }

      // Pre-mark images as loaded if their thumbnails are already in browser cache
      const nextInner = preloadedImages.slice(0, 4);
      const preMarked = {};
      [...innerImages, ...nextInner].forEach(item => {
        if (item.thumbnailUrl && preloadedUrlsRef.current.has(item.thumbnailUrl)) {
          preMarked[item.id] = true;
        }
      });
      setLoadedImages(preMarked);

      // Use preloaded images for immediate rotation
      setOuterImages([...innerImages]);
      setInnerImages(nextInner);

      // Remove the used preloaded images and keep the rest
      const remainingPreloaded = preloadedImages.slice(4);
      setPreloadedImages(remainingPreloaded);

      // If preloaded buffer is getting low, fetch more in background (fire-and-forget)
      if (remainingPreloaded.length < 12 && !isSearchMode) {
        fetchMoreImages(36).then(newImages => {
          preloadBrowserImages(newImages);
          setPreloadedImages(prev => [...prev, ...newImages]);
        });
      }
    } catch (error) {
      console.error("Error in manual rotation:", error);
    } finally {
      // Small delay before allowing next rotation to debounce rapid presses
      setTimeout(() => { isRotatingRef.current = false; }, 150);
    }
  };

  // Add keyboard event listeners for arrow key shortcuts and spacebar
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Handle spacebar for manual ring rotation
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault(); // Prevent page scroll
        forceNextRingRef.current();
        return;
      }
      
      // Only process arrow keys if we have outer images loaded
      if (outerImages.length !== 4) return;
      
      switch (event.key) {
        case 'ArrowUp':
          // Trigger animation for north
          triggerAnimation('north');
          // Save NORTH image (index 0)
          handleSaveImage(outerImages[0], event);
          break;
        case 'ArrowRight':
          // Trigger animation for east
          triggerAnimation('east');
          // Save EAST image (index 1)
          handleSaveImage(outerImages[1], event);
          break;
        case 'ArrowDown':
          // Trigger animation for south
          triggerAnimation('south');
          // Save SOUTH image (index 2)
          handleSaveImage(outerImages[2], event);
          break;
        case 'ArrowLeft':
          // Trigger animation for west
          triggerAnimation('west');
          // Save WEST image (index 3)
          handleSaveImage(outerImages[3], event);
          break;
        default:
          // Ignore other keys
          break;
      }
    };
    
    // Add keyboard event listener
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [outerImages]); // Re-add listener when outer images change

  // Swipe gesture detection for mobile — swipe in any direction to load next set
  const forceNextRingRef = useRef(forceNextRing);
  forceNextRingRef.current = forceNextRing;

  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 50) {
        forceNextRingRef.current();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Check for existing authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setUser(userData);
        
        // Verify token is still valid by fetching user data
        const verifyToken = async () => {
          try {
            const response = await fetch('/api/users/me', {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (!response.ok) {
              // Token invalid, remove it
              handleLogout();
            } else {
              // Update user data
              const freshUserData = await response.json();
              setUser(freshUserData);
            }
          } catch (error) {
            console.error('Error verifying token:', error);
          }
        };
        
        verifyToken();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        handleLogout();
      }
    }
  }, []);

  // Show help modal on first visit (any user, authenticated or not)
  useEffect(() => {
    const hasSeenHelp = localStorage.getItem('hasSeenHelpModal');
    if (!hasSeenHelp) {
      setShowHelpModal(true);
      localStorage.setItem('hasSeenHelpModal', 'true');
    }
  }, []);


  // Session countdown timer (only when timer is enabled)
  useEffect(() => {
    if (!timerEnabled) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    setTimeRemaining(60);
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerEnabled]);

  // Save session to backend and navigate to review
  const saveSessionAndNavigate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Bulk-save images to DB
      await Promise.all(sessionImages.map(item =>
        fetch('/api/saved-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({
            archiveId: item.id,
            title: item.name,
            description: item.description,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            fullSizeUrl: item.fullSizeUrl || null,
          }),
        }).catch(() => {})
      ));
      // Create session
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ images: sessionImages, title: sessionPrompt }),
      });
      const data = await response.json();
      if (response.ok) {
        navigate(`/s/${data.code}`);
      } else {
        navigate('/session-review', { state: { images: sessionImages } });
      }
    } catch (error) {
      console.error('Error saving session:', error);
      navigate('/session-review', { state: { images: sessionImages } });
    }
  }, [sessionImages, sessionPrompt, navigate]);

  // End session — navigate to review or reset
  const endSession = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (sessionImages.length > 0) {
      if (isAuthenticated) {
        await saveSessionAndNavigate();
      } else {
        // Not logged in — prompt to create account
        setPendingSessionEnd(true);
        setShowModal(true);
      }
    } else {
      setTimeRemaining(60);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [sessionImages, isAuthenticated, saveSessionAndNavigate]);

  // When timer reaches 0, end the session (only when timer is enabled)
  useEffect(() => {
    if (timerEnabled && timeRemaining === 0) {
      endSession();
    }
  }, [timeRemaining, endSession, timerEnabled]);

  // Mark an image as loaded
  const handleImageLoad = (imageId) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  // Handle image save
  const handleSaveImage = async (item, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Check if already saved in session
    const alreadySaved = sessionImages.find(img => img.id === item.id);
    if (alreadySaved) {
      return; // Prevent clicking if already saved
    }

    // Always add to session images immediately (no auth required)
    setSessionImages(prev => {
      const exists = prev.find(img => img.id === item.id);
      if (!exists) {
        const newImages = [...prev, item];
        // Persist to localStorage for unauthenticated users so images survive navigation
        if (!isAuthenticated) {
          localStorage.setItem('pendingSessionImages', JSON.stringify(newImages));
        }
        setTimeout(() => {
          if (sessionThumbnailsRef.current) {
            sessionThumbnailsRef.current.scrollTop = sessionThumbnailsRef.current.scrollHeight;
          }
        }, 0);
        return newImages;
      }
      return prev;
    });

    // If authenticated, also persist to DB
    if (isAuthenticated) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/saved-images', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            archiveId: item.id,
            title: item.name,
            description: item.description,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            fullSizeUrl: item.fullSizeUrl || null
          })
        });

        await response.json();
      } catch (error) {
        console.error('Error saving image to DB:', error);
      }
    }

    setNotification({ show: true, message: 'Image saved!' });
    setTimeout(() => setNotification({ show: false, message: '' }), 2000);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleAuthModalClose = async (success, userData) => {
    setShowModal(false);

    if (success && userData) {
      setIsAuthenticated(true);
      setUser(userData);

      // Save any pending session images from localStorage
      const pending = localStorage.getItem('pendingSessionImages');
      if (pending) {
        try {
          const pendingImages = JSON.parse(pending);
          const token = localStorage.getItem('token');
          await Promise.all(pendingImages.map(item =>
            fetch('/api/saved-images', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
              body: JSON.stringify({
                archiveId: item.id,
                title: item.name,
                description: item.description,
                url: item.url,
                thumbnailUrl: item.thumbnailUrl,
                fullSizeUrl: item.fullSizeUrl || null,
              }),
            }).catch(() => {})
          ));
          // Merge into current session state if still on Fountain
          setSessionImages(prev => {
            const merged = [...prev];
            pendingImages.forEach(img => {
              if (!merged.find(m => m.id === img.id)) merged.push(img);
            });
            return merged;
          });
        } catch (e) {
          console.error('Error saving pending images:', e);
        }
        localStorage.removeItem('pendingSessionImages');
      }

      if (pendingSessionEnd) {
        setPendingSessionEnd(false);
        saveSessionAndNavigate();
      }
    } else {
      setPendingSessionEnd(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Handle source checkbox changes
  const handleSourceChange = (source) => {
    setImageSources(prev => ({
      ...prev,
      [source]: !prev[source]
    }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchMode(true);
      fetchImages(searchQuery);
    }
  };

  // Toggle search panel
  const toggleSearchPanel = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Clear search and return to random mode
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setIsSearchExpanded(false);
    fetchImages();
  };

  // Toggle sidebar minimization
  const toggleSidebar = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  // Toggle session panel minimization
  const toggleSession = () => {
    setIsSessionMinimized(!isSessionMinimized);
  };

  // Toggle help modal
  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  // Toggle settings panel
  const toggleSettingsPanel = () => {
    setShowSettingsPanel(!showSettingsPanel);
  };

  // Toggle timer enabled
  const handleTimerToggle = () => {
    const newValue = !timerEnabled;
    setTimerEnabled(newValue);
    localStorage.setItem('timerEnabled', String(newValue));
  };

  if (loading && outerImages.length === 0 && innerImages.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  if (error && outerImages.length === 0 && innerImages.length === 0) {
    return <div className="error">Error: {error}</div>;
  }

  // Create directional positions for outer circle
  const outerDirections = ['north', 'east', 'south', 'west'];
  // Create directional positions for inner circle
  const innerDirections = ['north-inner', 'east-inner', 'south-inner', 'west-inner'];
  
  return (
    <div className="app-container">
      {notification.show && <Notification message={notification.message} />}

      {/* Sidebar with minimize functionality */}
      <div className={`sidebar ${isSidebarMinimized ? 'minimized' : ''}`}>
        {!isSidebarMinimized ? (
          <div className="sidebar-nav">
            <button className="nav-button minimize-button" onClick={toggleSidebar} title="Minimize sidebar">
              <ChevronLeft size={18} />
            </button>
            {isAuthenticated ? (
              <>
                <a href="/" className="nav-button" title="Home"><Target size={18} /></a>
                <a href={user ? `/${user.username}` : '/dashboard'} className="nav-button" title="Profile"><User size={18} /></a>
                <button className="nav-button" onClick={toggleSettingsPanel} title="Settings"><Settings size={18} /></button>
                <button className="nav-button" onClick={handleLogout} title="Logout"><LogOut size={18} /></button>
              </>
            ) : (
              <>
                <button className="nav-button" onClick={toggleModal} title="Login"><User size={18} /></button>
                <button className="nav-button" onClick={toggleSettingsPanel} title="Settings"><Settings size={18} /></button>
              </>
            )}
          </div>
        ) : (
          <button className="nav-button expand-button" onClick={toggleSidebar} title="Expand sidebar">
            <Menu size={18} />
          </button>
        )}
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Search Panel */}
        <div ref={searchPanelRef} className={`search-panel ${isSearchExpanded ? 'expanded' : 'collapsed'}`}>
          {!isSearchExpanded ? (
            // Collapsed state - just the search button
            <button type="button" onClick={toggleSearchPanel} className="search-button">
              <Search size={18} />
            </button>
          ) : (
            // Expanded state - full search form
            <form onSubmit={handleSearch} className="search-form">
              <div className={`search-input-wrapper ${isSearchMode ? 'search-active' : ''}`}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search images..."
                  className="search-input"
                  autoFocus
                />
              </div>
              <button type="submit" className="search-button">
                <Search size={18} />
              </button>
              {isSearchMode && (
                <button type="button" onClick={clearSearch} className="clear-button">
                  <X size={18} />
                </button>
              )}
            </form>
          )}
        </div>
        
        {/* Search Alpha Message */}
        {isSearchExpanded && (
          <div className="search-alpha-message">
            Search is in alpha!
          </div>
        )}

        {/* Help Button */}
        <div className="help-panel">
          <button type="button" onClick={toggleHelpModal} className="help-button" title="Help">
            <HelpCircle size={18} />
          </button>
        </div>

        {/* Session Panel — hide when sidebar is expanded to avoid overlap */}
        {isSidebarMinimized && (
          !isSessionMinimized ? (
            <div className="session-panel">
              <div className="session-title" onClick={toggleSession} title="Minimize session">
                Session
              </div>
              {sessionImages.length > 0 ? (
                <div ref={sessionThumbnailsRef} className="session-thumbnails">
                  {sessionImages.map((item, index) => (
                    <div
                      key={`session-${item.id}-${index}`}
                      className="session-thumbnail"
                      onClick={() => window.open(item.url, '_blank')}
                      title={item.name}
                    >
                      <img
                        src={item.thumbnailUrl}
                        alt={item.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://archive.org/images/notfound.png";
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="session-empty">
                  Save images to see them here
                </div>
              )}
            </div>
          ) : (
            <button className="session-button-minimized" onClick={toggleSession} title="Show session">
              <Save size={18} />
            </button>
          )
        )}

        {/* Fountain Container */}
        <div className="fountain-container">
          <div className="background-circle"></div>
          <div className="inner-background-circle"></div>
          <div className="compass-layout">
            {/* Outer Circle - Current outer images */}
            {outerImages.map((item, index) => {
              const direction = outerDirections[index];
              const isAnimating = animationStates[direction];
              const isAlreadySaved = sessionImages.find(img => img.id === item.id);
              
              return (
                <div
                  key={`outer-${item.id}-${index}`}
                  className={`archive-item ${direction} ${isAnimating ? 'nudge tint' : ''} ${isAlreadySaved ? 'saved' : ''}`}
                  onClick={(e) => handleSaveImage(item, e)}
                >
                  {!loadedImages[item.id] && <div className="loading-spinner"></div>}
                  <img
                    src={item.thumbnailUrl}
                    alt={item.name}
                    className={loadedImages[item.id] ? "image-loaded" : "image-loading"}
                    onLoad={() => handleImageLoad(item.id)}
                    onError={(e) => {
                      const img = e.target;
                      if (!img.src.includes('/api/proxy-image')) {
                        img.src = `/api/proxy-image?url=${encodeURIComponent(item.thumbnailUrl)}`;
                      } else {
                        img.onerror = null;
                        img.src = "https://archive.org/images/notfound.png";
                        handleImageLoad(item.id);
                      }
                    }}
                  />
                  {isAlreadySaved && (
                    <div className="saved-overlay">
                      <Save className="save-icon" size={48} />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Inner Circle - Current inner images */}
            {innerImages.map((item, index) => {
              const isAlreadySaved = sessionImages.find(img => img.id === item.id);

              return (
                <div
                  key={`inner-${item.id}-${index}`}
                  className={`archive-item inner ${innerDirections[index]} ${isAlreadySaved ? 'saved' : ''}`}
                  onClick={(e) => handleSaveImage(item, e)}
                >
                  {!loadedImages[item.id] && <div className="loading-spinner"></div>}
                  <img
                    src={item.thumbnailUrl}
                    alt={item.name}
                    className={loadedImages[item.id] ? "image-loaded" : "image-loading"}
                    onLoad={() => handleImageLoad(item.id)}
                    onError={(e) => {
                      const img = e.target;
                      if (!img.src.includes('/api/proxy-image')) {
                        img.src = `/api/proxy-image?url=${encodeURIComponent(item.thumbnailUrl)}`;
                      } else {
                        img.onerror = null;
                        img.src = "https://archive.org/images/notfound.png";
                        handleImageLoad(item.id);
                      }
                    }}
                  />
                  {isAlreadySaved && (
                    <div className="saved-overlay">
                      <Save className="save-icon" size={48} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Help Modal */}
      {showHelpModal && (
        <div className="modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-button" onClick={() => setShowHelpModal(false)}>
              <X size={20} />
            </button>
            <h3>How to Use</h3>
            <div className="help-content">
              <p><strong>Click or use your arrow keys</strong> to save images to your boards:</p>
              <ul>
                <li><kbd>↑</kbd> Save the top image</li>
                <li><kbd>→</kbd> Save the right image</li>
                <li><kbd>↓</kbd> Save the bottom image</li>
                <li><kbd>←</kbd> Save the left image</li>
              </ul>
              <p>Press <kbd>Space</kbd> to load the next set of images.</p>
              <p><strong>On mobile:</strong> Swipe in any direction to load the next set.</p>
              <p>You'll be prompted to save your session at the end.</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettingsPanel && (
        <div className="settings-panel">
          <div className="settings-header">
            <span className="settings-title">Settings</span>
            <button className="settings-close-button" onClick={toggleSettingsPanel}>
              <X size={16} />
            </button>
          </div>
          <div className="settings-content">
            <div className="settings-item">
              <span className="settings-label">Session Timer</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={timerEnabled}
                  onChange={handleTimerToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="settings-item">
              <button className="settings-instructions-button" onClick={() => setShowInstructionsModal(true)}>
                <BookOpen size={16} />
                <span>Instructions</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Modal */}
      {showInstructionsModal && (
        <div className="modal-overlay" onClick={() => setShowInstructionsModal(false)}>
          <div className="help-modal-content instructions-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-button" onClick={() => setShowInstructionsModal(false)}>
              <X size={20} />
            </button>
            <h3>Welcome to Endotica</h3>
            <div className="help-content">
              <p>Endotica is an image discovery and curation platform. Browse images from across the internet, save the ones that resonate with you, and organize them into personal boards.</p>

              <p><strong>The Fountain</strong></p>
              <p>The main screen shows images arranged in a circular layout. New images rotate in automatically every few seconds.</p>
              <ul>
                <li><kbd>↑</kbd> <kbd>→</kbd> <kbd>↓</kbd> <kbd>←</kbd> Arrow keys save the image in that direction</li>
                <li><kbd>Space</kbd> loads the next set of images</li>
                <li>Click any image to save it</li>
                <li>On mobile, swipe to load the next set</li>
              </ul>

              <p><strong>Sessions</strong></p>
              <p>When the session timer is enabled in Settings, you get 60 seconds to collect images inspired by a random prompt word. When time runs out, you'll see a review of everything you saved.</p>

              <p><strong>Boards</strong></p>
              <p>Visit your profile to organize saved images into boards — personal collections you can name, rearrange, and share.</p>

              <p><strong>Other Modes</strong></p>
              <ul>
                <li><strong>Tinder</strong> — swipe cards left or right to save or skip</li>
                <li><strong>Implicit</strong> — two images side by side, pick left or right</li>
              </ul>

              <p><strong>Search</strong></p>
              <p>Use the search icon (top right) to find images by keyword. Currently searches Archive.org.</p>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showModal}
        onClose={handleAuthModalClose}
      />

      {/* Session Timer Line (only when timer is enabled) */}
      {timerEnabled && (
        <div className="session-timer-line" style={{ width: `${(timeRemaining / 60) * 100}%` }} />
      )}

      {/* Done Button (only when timer is enabled) */}
      {timerEnabled && (
        <button className="session-done-button" onClick={endSession}>
          Done
        </button>
      )}
    </div>
  );
}

export default Fountain;